import React, { useState, useEffect } from "react";
import { database } from "../../firebase/config";

const toDateObject = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate();
  }
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const isBonusActiveForMonth = (bonus, targetYear, targetMonth) => {
  if (!bonus?.scadenza) return true;
  const d = toDateObject(bonus.scadenza);
  if (!d) return true;
  const scadYear = d.getFullYear();
  const scadMonth = d.getMonth();
  if (scadYear > targetYear) return true;
  if (scadYear < targetYear) return false;
  return scadMonth >= targetMonth;
};

export default function BonusManager({ user, month, year }) {
  const [availableBonuses, setAvailableBonuses] = useState([]);
  const [confirmedBonuses, setConfirmedBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [hasExistingConfirmation, setHasExistingConfirmation] = useState(false);

  useEffect(() => {
    if (!user || !user.anagrafica || !user.anagrafica.bonus) {
      setAvailableBonuses([]);
      setConfirmedBonuses([]);
      return;
    }

    // 1. Calcola i bonus potenzialmente attivi dall'anagrafica
    const potential = user.anagrafica.bonus.filter((b) =>
      isBonusActiveForMonth(b, year, month),
    );

    setAvailableBonuses(potential);

    // 2. Recupera conferme esistenti
    const fetchConfirmation = async () => {
      setLoading(true);
      const id = `${user.id}_${year}_${month}`;
      setDocId(id);
      try {
        const doc = await database.collection("bonus_mensili").doc(id).get();
        if (doc.exists) {
          const data = doc.data();
          setConfirmedBonuses(data.bonuses || []);
          setHasExistingConfirmation(true);
        } else {
          // Default: tutti i bonus attivi sono confermati se non c'è salvataggio precedente
          setConfirmedBonuses(potential);
          setHasExistingConfirmation(false);
        }
      } catch (err) {
        console.error("Errore nel recupero bonus:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfirmation();
  }, [user, month, year]);

  const handleToggle = (bonus) => {
    setConfirmedBonuses((prev) => {
      // Usa l'ID come chiave univoca se disponibile, altrimenti fallback su nome (per retrocompatibilità)
      const bonusId = bonus.id || bonus.nome;

      const exists = prev.find((b) => (b.id || b.nome) === bonusId);
      if (exists) {
        return prev.filter((b) => (b.id || b.nome) !== bonusId);
      } else {
        return [...prev, bonus];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await database
        .collection("bonus_mensili")
        .doc(docId)
        .set({
          userId: user.id,
          anno: year,
          mese: month,
          bonuses: confirmedBonuses,
          updatedAt: new Date(),
          userDisplayName: user.displayName || user.email,
          confirmed: true,
        });
      setHasExistingConfirmation(true);
      alert("Bonus confermati salvati con successo!");
    } catch (err) {
      console.error("Errore nel salvataggio bonus:", err);
      alert("Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="admin-card" style={{ borderLeft: "4px solid #ffc107" }}>
      <div className="admin-card-header">
        <h3>💰 Gestione Bonus Mensili</h3>
      </div>
      <div className="admin-card-content">
        <div className="admin-section-title">
          Bonus Applicabili per {user.anagrafica?.nome || user.displayName}
        </div>

        {loading ? (
          <p>Caricamento bonus...</p>
        ) : availableBonuses.length === 0 ? (
          <p style={{ color: "#666", fontStyle: "italic" }}>
            Nessun bonus attivo in anagrafica per questo periodo.
          </p>
        ) : (
          <div style={{ marginBottom: "15px" }}>
            {availableBonuses.map((bonus, index) => {
              const isConfirmed = confirmedBonuses.some(
                (b) => (b.id || b.nome) === (bonus.id || bonus.nome),
              );
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px",
                    borderBottom: "1px solid #eee",
                    backgroundColor: isConfirmed ? "#f0fdf4" : "#fff",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isConfirmed}
                    onChange={() => handleToggle(bonus)}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginRight: "10px",
                      cursor: "pointer",
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "bold" }}>{bonus.nome}</div>
                    <div style={{ fontSize: "0.85rem", color: "#666" }}>
                      Importo: € {parseFloat(bonus.importo).toFixed(2)}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      backgroundColor: isConfirmed ? "#d1e7dd" : "#f8d7da",
                      color: isConfirmed ? "#0f5132" : "#842029",
                    }}
                  >
                    {isConfirmed ? "Applicato" : "Escluso"}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {availableBonuses.length > 0 && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#ffc107",
              color: "#000",
              border: "none",
              borderRadius: "4px",
              fontWeight: "bold",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Salvataggio..." : "Salva Conferma Bonus"}
          </button>
        )}

        {hasExistingConfirmation && (
          <div
            style={{
              marginTop: "10px",
              fontSize: "0.8rem",
              color: "#28a745",
              textAlign: "center",
            }}
          >
            ✓ Configurazione salvata presente
          </div>
        )}
      </div>
    </div>
  );
}
